import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Box, Paper, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, 
  TableRow, Chip, IconButton, Dialog, DialogActions, 
  DialogContent, DialogTitle, TextField, Grid, FormControlLabel,
  Switch, MenuItem, Select, FormControl, InputLabel, Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { 
  getAIModels, 
  createAIModel, 
  updateAIModel, 
  deleteAIModel, 
  checkAIModelHealth 
} from '../services/aiService';

const AIModels = () => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [currentModel, setCurrentModel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    modelId: '',
    apiKey: '',
    description: '',
    capabilities: {},
    config: {},
    isActive: true,
    isDefault: false
  });
  const [alert, setAlert] = useState({ show: false, severity: 'info', message: '' });
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Load models on component mount
  useEffect(() => {
    fetchModels();
  }, []);
  
  const fetchModels = async () => {
    setLoading(true);
    try {
      const response = await getAIModels();
      setModels(response.data || []);
    } catch (error) {
      console.error('Error fetching AI models:', error);
      showAlert('Failed to load AI models', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleJsonChange = (field) => (e) => {
    try {
      // Try to parse the JSON input
      const jsonValue = e.target.value.trim() === '' ? {} : JSON.parse(e.target.value);
      setFormData({
        ...formData,
        [field]: jsonValue
      });
    } catch (error) {
      // If not valid JSON, store as string to allow user to continue editing
      setFormData({
        ...formData,
        [field]: e.target.value
      });
    }
  };
  
  const openAddForm = () => {
    setFormData({
      name: '',
      provider: '',
      modelId: '',
      apiKey: '',
      description: '',
      capabilities: {},
      config: {},
      isActive: true,
      isDefault: false
    });
    setFormMode('add');
    setFormOpen(true);
  };
  
  const openEditForm = (model) => {
    setCurrentModel(model);
    setFormData({
      name: model.name,
      provider: model.provider,
      modelId: model.model_id, // Backend uses snake_case
      apiKey: '',
      description: model.description || '',
      capabilities: model.capabilities || {},
      config: model.config || {},
      isActive: model.is_active, // Backend uses snake_case
      isDefault: model.is_default // Backend uses snake_case
    });
    setFormMode('edit');
    setFormOpen(true);
  };
  
  const openDeleteConfirm = (model) => {
    setCurrentModel(model);
    setConfirmDelete(true);
  };
  
  const closeForm = () => {
    setFormOpen(false);
    setCurrentModel(null);
  };
  
  const closeConfirmDelete = () => {
    setConfirmDelete(false);
    setCurrentModel(null);
  };
  
  const handleHealthCheck = async (id) => {
    try {
      await checkAIModelHealth(id);
      // Refresh the list after health check
      fetchModels();
      showAlert('Health check completed', 'success');
    } catch (error) {
      console.error('Error checking model health:', error);
      showAlert('Failed to perform health check', 'error');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validate form
      if (!formData.name || !formData.provider || !formData.modelId) {
        showAlert('Please fill all required fields', 'error');
        return;
      }
      
      // Ensure capabilities and config are valid JSON objects
      let validForm = true;
      try {
        if (typeof formData.capabilities === 'string') {
          formData.capabilities = JSON.parse(formData.capabilities);
        }
        if (typeof formData.config === 'string') {
          formData.config = JSON.parse(formData.config);
        }
      } catch (error) {
        showAlert('Invalid JSON in capabilities or config', 'error');
        validForm = false;
      }
      
      if (!validForm) return;
      
      if (formMode === 'add') {
        // Create new model
        await createAIModel(formData);
        showAlert('AI model created successfully', 'success');
      } else {
        // Update existing model
        await updateAIModel(currentModel.id, formData);
        showAlert('AI model updated successfully', 'success');
      }
      
      // Close form and refresh list
      closeForm();
      fetchModels();
    } catch (error) {
      console.error('Error saving AI model:', error);
      showAlert(error.response?.data?.error || 'Failed to save AI model', 'error');
    }
  };
  
  const handleDelete = async () => {
    try {
      await deleteAIModel(currentModel.id);
      showAlert('AI model deleted successfully', 'success');
      fetchModels();
      closeConfirmDelete();
    } catch (error) {
      console.error('Error deleting AI model:', error);
      showAlert(error.response?.data?.error || 'Failed to delete AI model', 'error');
    }
  };
  
  const showAlert = (message, severity = 'info') => {
    setAlert({ show: true, severity, message });
    
    // Auto-hide the alert after 5 seconds
    setTimeout(() => {
      setAlert({ ...alert, show: false });
    }, 5000);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'degraded':
        return 'warning';
      default:
        return 'default';
    }
  };
  
  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          AI Models
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage AI models used for call transcription and analysis
        </Typography>
        
        {alert.show && (
          <Alert severity={alert.severity} sx={{ my: 2 }}>
            {alert.message}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={openAddForm}
          >
            Add Model
          </Button>
        </Box>
        
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Model ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Last Check</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : models.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No AI models found
                    </TableCell>
                  </TableRow>
                ) : (
                  models.map((model) => (
                    <TableRow key={model.id}>
                      <TableCell>{model.name}</TableCell>
                      <TableCell>{model.provider}</TableCell>
                      <TableCell>{model.model_id}</TableCell>
                      <TableCell>
                        <Chip 
                          label={model.status} 
                          color={getStatusColor(model.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {model.is_default ? (
                          <Chip label="Default" color="primary" size="small" />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {model.is_active ? (
                          <Chip label="Active" color="success" size="small" />
                        ) : (
                          <Chip label="Inactive" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {model.last_health_check ? 
                          (() => {
                            const date = new Date(model.last_health_check);
                            return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                          })() : 
                          'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => openEditForm(model)}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openDeleteConfirm(model)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleHealthCheck(model.id)}
                          title="Check Health"
                        >
                          <HealthAndSafetyIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      
      {/* Add/Edit Form Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {formMode === 'add' ? 'Add AI Model' : 'Edit AI Model'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel id="provider-label">Provider</InputLabel>
                  <Select
                    labelId="provider-label"
                    name="provider"
                    value={formData.provider}
                    onChange={handleFormChange}
                    required
                  >
                    <MenuItem value="OpenAI">OpenAI</MenuItem>
                    <MenuItem value="Anthropic">Anthropic</MenuItem>
                    <MenuItem value="Google">Google</MenuItem>
                    <MenuItem value="Mistral">Mistral</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Model ID"
                  name="modelId"
                  value={formData.modelId}
                  onChange={handleFormChange}
                  required
                  margin="normal"
                  helperText="Specific model identifier (e.g., gpt-4, claude-3, gemini-pro)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key (will be encrypted)"
                  name="apiKey"
                  type="password"
                  value={formData.apiKey}
                  onChange={handleFormChange}
                  helperText={formMode === 'edit' ? "Leave blank to keep existing key" : ""}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capabilities (JSON)"
                  name="capabilities"
                  value={typeof formData.capabilities === 'object' ? 
                    JSON.stringify(formData.capabilities, null, 2) : 
                    formData.capabilities}
                  onChange={handleJsonChange('capabilities')}
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Configuration (JSON)"
                  name="config"
                  value={typeof formData.config === 'object' ? 
                    JSON.stringify(formData.config, null, 2) : 
                    formData.config}
                  onChange={handleJsonChange('config')}
                  multiline
                  rows={4}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleFormChange}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isDefault}
                      onChange={handleFormChange}
                      name="isDefault"
                    />
                  }
                  label="Set as Default"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
            >
              {formMode === 'add' ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={closeConfirmDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the AI model "{currentModel?.name}"? 
          This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDelete}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AIModels; 